import { ERR_FAILED, newError } from "../utils/error";
import { isString } from "../utils/string";
import { ItemUpdateFlags } from "./BaseItem";
import { BaseItemManager } from "./BaseItemManager";
import { ViewArticleItem } from "./ViewArticleItem";
import { ViewFolderItem } from "./ViewFolderItem";
import { ViewItem } from "./ViewItem";
import { ViewSetArticleItem } from "./ViewSetArticleItem";
import { ViewTextItem } from "./ViewTextItem";
import { ViewUserArticleItem } from "./ViewUserArticleItem";
export class View extends BaseItemManager {
    get viewId() {
        return (this.mViewId);
    }
    get viewConfig() {
        return (this.mViewConfig);
    }
    get basket() {
        return (this.mBasket);
    }
    get generatedImageResolution() {
        return (this.mBasket.generatedImageResolution);
    }
    get includeCalculationErrorsInInconsistencies() {
        return (this.mBasket.includeCalculationErrorsInInconsistencies);
    }
    /**
     * Can be used to define extra pricing information which sould be part of the
     * `ArticleProperties.priceInfo`. See EAIWS documentation for further information.
     */
    get extraPriceInfoSelectors() {
        return (this.mExtraPriceInfoSelectors);
    }
    set extraPriceInfoSelectors(pSelectors) {
        this.mExtraPriceInfoSelectors = pSelectors;
        void this.mRoot.updateItem(ItemUpdateFlags.ItemProps | ItemUpdateFlags.IncludeChildren);
    }
    async update(pForce = false) {
        //TODO: improve View.update()
        if (this.mIsUpToDate && !pForce)
            return;
        this.mIsUpToDate = true;
        this.mBasketItemIdMap.clear();
        await this.mRoot.updateItem(ItemUpdateFlags.Item | ItemUpdateFlags.IncludeChildren);
    }
    /** Returns the view item for the given basket item id */
    getItemByBasketId(pBasketItemId) {
        const tItemId = this.mBasketItemIdMap.get(pBasketItemId);
        if (tItemId == null)
            return (null);
        return (this.getItemById(tItemId));
    }
    //// internal /////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _getPricingProcedure() {
        return (this.mBasket.calculation.pricingProcedure);
    }
    /** @internal */
    _getGeneratedImageOptions() {
        return (this.mBasket._getGeneratedImageOptions());
    }
    /** @internal */
    _getViewConfig() {
        return (this.mViewConfig);
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    createItem(pParent, pChildIndex, pEaiwsItem) {
        let tItem;
        switch (pEaiwsItem.itemType) {
            case ("Folder"):
                tItem = new ViewFolderItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
            case ("PartialPlanning"):
            case ("Aggregate"):
            case ("Article"):
                tItem = new ViewArticleItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
            case ("UserArticle"):
                tItem = new ViewUserArticleItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
            case ("SetArticle"):
                tItem = new ViewSetArticleItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
            case ("Text"):
                tItem = new ViewTextItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
            default:
                tItem = new ViewItem(this, pParent, pChildIndex, pEaiwsItem);
                break;
        }
        return (tItem);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    static async _CreateView(pBasket, pViewIdOrConfig) {
        const tView = new View(pBasket, pViewIdOrConfig);
        await tView.initialize();
        return (tView);
    }
    /** @internal */
    _dispose() {
        this.internalClear();
        this.mBasketItemIdMap.clear();
    }
    /** @internal */
    _addEaiwsItemToCache(pItem) {
        super._addEaiwsItemToCache(pItem);
        if (pItem.basketItemIds != null) {
            for (const tId of pItem.basketItemIds) {
                this.mBasketItemIdMap.set(tId, pItem.itemId);
            }
        }
    }
    /** @internal */
    async _updateViewItem(pChangedBasketItem, pFlags) {
        let tFlags = pFlags & ~ItemUpdateFlags.IncludeChildren; //remove IncludeChildren flag
        if ((tFlags & ItemUpdateFlags.Item) != 0) {
            tFlags = tFlags & ~ItemUpdateFlags.Item; //remove Item flag
            this.mIsUpToDate = false;
        }
        const tItem = this.getItemByBasketId(pChangedBasketItem.id);
        if (tItem != null) {
            //reset item props if calculation has changed because they inlude the priceInfo
            if ((tFlags & ItemUpdateFlags.Calculation) != 0 && this._getPricingProcedure() != null)
                tFlags = tFlags | ItemUpdateFlags.ItemProps;
            await tItem.updateItem(tFlags);
        }
        else {
            this.mIsUpToDate = false; //item not found perform full update
        }
    }
    constructor(pBasket, pViewIdOrConfig) {
        super(pBasket.session);
        this.mBasket = pBasket;
        if (isString(pViewIdOrConfig)) {
            this.mViewId = pViewIdOrConfig;
            this.mViewConfig = undefined; //see initialize()
        }
        else {
            this.mViewId = pViewIdOrConfig.viewId;
            this.mViewConfig = pViewIdOrConfig;
        }
        this.mBasketItemIdMap = new Map();
        this.mIsUpToDate = true;
    }
    async initialize() {
        if (this.mViewConfig == null) {
            const tConfigs = await this.mSession.basket.getBasketViewConfigs([this.mViewId]);
            if (tConfigs.length !== 1)
                throw newError(ERR_FAILED, "Failed to get basket view config.");
            this.mViewConfig = tConfigs[0];
        }
        await this.loadRootFolder();
    }
}
